<template>
  <div class="json-view">
    <!-- Title (always visible) -->
    <div v-if="title" class="flex items-center justify-between mb-2">
      <span
        @click="toggleExpansion"
        class="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer hover:text-gray-900 dark:hover:text-gray-100"
      >
        {{ title }}
      </span>
      <button
        @click="toggleExpansion"
        class="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
      >
        <vue-feather
          :type="isExpanded ? 'chevron-up' : 'chevron-down'"
          size="14"
        />
        {{ isExpanded ? "Collapse" : "Expand" }}
      </button>
    </div>

    <!-- Collapsed View -->
    <div v-if="!isExpanded" class="flex items-center justify-between">
      <div
        @click="toggleExpansion"
        class="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer hover:text-gray-800 dark:hover:text-gray-200"
      >
        <vue-feather :type="getIconType()" size="16" />
        <span>{{ getSummaryText() }}</span>
      </div>
      <button
        v-if="!title"
        @click="toggleExpansion"
        class="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
      >
        <vue-feather type="chevron-down" size="14" />
        Expand
      </button>
    </div>

    <!-- Expanded View -->
    <div v-else class="space-y-2">
      <!-- JSON Content -->
      <div
        class="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-600"
      >
        <div
          v-if="isPrimitive"
          class="text-sm text-gray-900 dark:text-gray-100"
        >
          <span class="font-mono">{{ formatValue(data) }}</span>
        </div>

        <div v-else-if="isArray" class="space-y-2">
          <div
            v-for="(item, index) in data"
            :key="index"
            class="border-l-2 border-green-200 dark:border-green-800 pl-3"
          >
            <div class="flex items-start gap-2">
              <span
                class="text-sm font-medium text-green-600 dark:text-green-400 min-w-0 flex-shrink-0"
              >
                [{{ index }}]:
              </span>
              <div class="flex-1">
                <JSONView
                  v-if="!isPrimitiveValue(item)"
                  :data="item"
                  :expanded="false"
                  :show-toggle="false"
                  :title="`Item ${index}`"
                />
                <span
                  v-else
                  class="text-sm text-gray-900 dark:text-gray-100 font-mono"
                >
                  {{ formatValue(item) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div v-else-if="isObject" class="space-y-2">
          <div
            v-for="(value, key) in data"
            :key="key"
            class="border-l-2 border-blue-200 dark:border-blue-800 pl-3"
          >
            <div class="flex items-start gap-2">
              <span
                class="text-sm font-medium text-blue-600 dark:text-blue-400 min-w-0 flex-shrink-0"
              >
                {{ key }}:
              </span>
              <div class="flex-1">
                <JSONView
                  v-if="!isPrimitiveValue(value)"
                  :data="value"
                  :expanded="false"
                  :show-toggle="false"
                  :title="key"
                />
                <span
                  v-else
                  class="text-sm text-gray-900 dark:text-gray-100 font-mono"
                >
                  {{ formatValue(value) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Raw JSON Fallback -->
        <div
          v-if="showRawJson"
          class="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600"
        >
          <div class="flex items-center justify-between mb-2">
            <span
              @click="toggleRawJson"
              class="text-xs font-semibold text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-700 dark:hover:text-gray-300"
            >
              Raw JSON
            </span>
            <button
              @click="toggleRawJson"
              class="inline-flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
            >
              <vue-feather
                :type="showRawJsonContent ? 'chevron-up' : 'chevron-down'"
                size="14"
              />
              {{ showRawJsonContent ? "Hide" : "Show" }}
            </button>
          </div>

          <div
            v-if="showRawJsonContent"
            class="bg-gray-900 rounded-lg p-4 overflow-x-auto"
          >
            <pre class="text-sm text-green-400 font-mono whitespace-pre-wrap">{{
              JSON.stringify(data, null, 2)
            }}</pre>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from "vue";

export default {
  name: "JSONView",
  props: {
    data: {
      type: [Object, Array, String, Number, Boolean, null],
      required: true,
    },
    expanded: {
      type: Boolean,
      default: false,
    },
    showToggle: {
      type: Boolean,
      default: true,
    },
    title: {
      type: String,
      default: "",
    },
    showRawJson: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["toggle"],
  setup(props, { emit }) {
    const localExpanded = ref(props.expanded);
    const showRawJsonContent = ref(false);

    const isPrimitive = computed(() => {
      return (
        props.data === null ||
        props.data === undefined ||
        typeof props.data !== "object"
      );
    });

    const isArray = computed(() => {
      return Array.isArray(props.data);
    });

    const isObject = computed(() => {
      return !isPrimitive.value && !isArray.value;
    });

    const isExpanded = computed({
      get: () => localExpanded.value,
      set: (value) => {
        localExpanded.value = value;
        emit("toggle", value);
      },
    });

    const toggleExpansion = () => {
      isExpanded.value = !isExpanded.value;
    };

    const toggleRawJson = () => {
      showRawJsonContent.value = !showRawJsonContent.value;
    };

    const getIconType = () => {
      if (isArray.value) return "list";
      if (isObject.value) return "file-text";
      return "hash";
    };

    const getSummaryText = () => {
      if (isPrimitive.value) {
        return `${typeof props.data}: ${formatValue(props.data)}`;
      }
      if (isArray.value) {
        return `${props.data.length} items`;
      }
      if (isObject.value) {
        return `${Object.keys(props.data).length} properties`;
      }
      return "Unknown data type";
    };

    const formatValue = (value) => {
      if (value === null) return "null";
      if (value === undefined) return "undefined";
      if (typeof value === "string") return `"${value}"`;
      if (typeof value === "boolean") return value.toString();
      if (typeof value === "number") return value.toString();
      return JSON.stringify(value);
    };

    const isPrimitiveValue = (value) => {
      return value === null || value === undefined || typeof value !== "object";
    };

    return {
      isExpanded,
      showRawJsonContent,
      isPrimitive,
      isArray,
      isObject,
      toggleExpansion,
      toggleRawJson,
      getIconType,
      getSummaryText,
      formatValue,
      isPrimitiveValue,
    };
  },
};
</script>

<style scoped>
.json-view {
  @apply w-full;
}
</style>
